import Axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
// components
import { AuthProvider } from './auth';
import { PrivateRoute } from './privateRoute';
import { Routes, Route } from 'react-router-dom';
import { View } from 'components/lib';
import { MaintenanceNoticeBanner } from 'components/maintenance-notice/maintenance-notice-banner';
// 404
import { NotFound } from 'views/system/NotFound';
// tailwind css (compiled by Vite/PostCSS from the source entry)
import '../css/input.css';
import ProjectContextProvider from 'contexts/ProjectContext';
import GridContextProvider from 'contexts/GridContext';
import GeodataContextProvider from 'contexts/GeodataContext';
import SimulationContextProvider from 'contexts/SimulationContext';
import ValidationContextProvider from 'contexts/ValidationContext';
import LastAddedItemContextProvider from 'contexts/LastAddedItemContext';
import CurrentProjectContextProvider from 'contexts/CurrentProjectContext';
import ProjectAddExitContextProvider from 'contexts/ProjectAddExitContext';
import ValidationMinMaxContextProvider from 'contexts/ValidationMinMaxContext';
import ClosestStationContextProvider from 'contexts/ClosestStationContext';
import RunContextProvider from 'contexts/RunContext';
import {
  dismissMaintenanceNotice,
  fetchNextMaintenanceNotice,
  getDismissedMaintenanceNoticeId,
  isMaintenanceResponse,
  redirectToMaintenance,
  setupMaintenanceInterceptor,
} from 'api/maintenanceApi';
import * as Sentry from '@sentry/react';
import accountRoutes from 'routes/account';
import appRoutes from 'routes/app';
import authRoutes from 'routes/auth';
import maintenanceRoutes from 'routes/maintenance';
// settings
const VERSION = import.meta.env.VITE_VERSION || 'undefined';

interface AppRoute {
  path: string;
  view: React.ComponentType<Record<string, unknown>>;
  layout?: 'app' | 'home' | 'auth' | 'onboarding' | 'plain';
  title: string;
  permission?: string;
  requireActivePlan?: boolean;
  requireNoActivePlan?: boolean;
}

const routes = [
  // for opening page
  // {
  //   path: '/',
  //   view: OpeningPage,
  //   layout: null,
  //   title: 'Opening Page',
  // },
  ...accountRoutes,
  ...appRoutes,
  ...authRoutes,
  ...maintenanceRoutes,
];
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

interface MaintenanceNotice {
  id: string;
  title: string;
  description: string | null;
  startAt: Date;
  probableEndAt: Date | null;
  createdAt?: Date;
  updatedAt?: Date;
}

export default function App() {
  const [maintenanceNotice, setMaintenanceNotice] = useState<MaintenanceNotice | null>(null);
  const [maintenanceNoticeVisible, setMaintenanceNoticeVisible] = useState(false);

  Axios.defaults.baseURL = import.meta.env.VITE_SERVER_URL || window.origin;
  Axios.defaults.withCredentials = true;
  delete Axios.defaults.headers.common['Authorization'];
  setupMaintenanceInterceptor();

  const shouldShowNotice = useMemo(() => {
    if (!maintenanceNotice?.id) return false;
    const dismissedId = getDismissedMaintenanceNoticeId();
    return dismissedId !== maintenanceNotice.id;
  }, [maintenanceNotice]);

  const handleDismissMaintenanceNotice = useCallback(
    (id?: string | null) => {
      dismissMaintenanceNotice(id);
      setMaintenanceNoticeVisible(false);
    },
    [setMaintenanceNoticeVisible]
  );

  useEffect(() => {
    let cancelled = false;

    async function loadMaintenanceNotice() {
      try {
        const nextNotice = await fetchNextMaintenanceNotice();
        if (cancelled) return;
        setMaintenanceNotice(nextNotice);
      } catch (error) {
        if (isMaintenanceResponse(error as { status?: number })) {
          if (!cancelled) redirectToMaintenance();
          return;
        }

        // The banner is best-effort; avoid blocking app startup on errors.
        console.error('Failed to load maintenance notice:', error);
        if (!cancelled) {
          setMaintenanceNotice(null);
        }
      }
    }

    loadMaintenanceNotice();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setMaintenanceNoticeVisible(shouldShowNotice);
  }, [shouldShowNotice]);

  // render the routes
  return (
    <>
      {maintenanceNoticeVisible && (
        <MaintenanceNoticeBanner notice={maintenanceNotice} onDismiss={handleDismissMaintenanceNotice} />
      )}
      <span id="version-info">{VERSION}</span>
      <div id="dashboard">
        <AuthProvider>
          <ClosestStationContextProvider>
            <ValidationMinMaxContextProvider>
              <ProjectAddExitContextProvider>
                <CurrentProjectContextProvider>
                  <RunContextProvider>
                    <LastAddedItemContextProvider>
                      <ValidationContextProvider>
                        <ProjectContextProvider>
                          <GridContextProvider>
                            <GeodataContextProvider>
                              <SimulationContextProvider>
                                <SentryRoutes>
                                  {(routes as unknown as AppRoute[]).map((route) => {
                                    // for opening page start
                                    // const RouteContent = route.layout ? (
                                    //   <View
                                    //     display={route.view}
                                    //     layout={route.layout}
                                    //     title={route.title}
                                    //   />
                                    // ) : (
                                    //   <route.view />
                                    // );
                                    // for opening page end
                                    return (
                                      <Route
                                        key={route.path}
                                        path={route.path}
                                        element={
                                          <PrivateRoute
                                            permission={route.permission}
                                            requireActivePlan={route.requireActivePlan}
                                            requireNoActivePlan={route.requireNoActivePlan}
                                          >
                                            <View display={route.view} layout={route.layout} title={route.title} />

                                            {/* {RouteContent} */}
                                          </PrivateRoute>
                                        }
                                      />
                                    );
                                  })}

                                  {/* 404 */}
                                  <Route
                                    path="*"
                                    element={<View display={NotFound} layout="plain" title="404 Not Found" />}
                                  />
                                </SentryRoutes>
                              </SimulationContextProvider>
                            </GeodataContextProvider>
                          </GridContextProvider>
                        </ProjectContextProvider>
                      </ValidationContextProvider>
                    </LastAddedItemContextProvider>
                  </RunContextProvider>
                </CurrentProjectContextProvider>
              </ProjectAddExitContextProvider>
            </ValidationMinMaxContextProvider>
          </ClosestStationContextProvider>
        </AuthProvider>
      </div>
    </>
  );
}
